import { Link } from 'react-router-dom';
import './legalPage.css';

export default function PrivacyPolicyPage() {
  return (
    <div className="legalPageDiv">
      <div className="legalPageHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h1 className="legalPageTitle">Privacy Policy</h1>
        <p className="legalPageDate">Last updated: April 25, 2026</p>
      </div>

      <div className="legalPageBody">
        <p>
          Effectual Tools ("Company", "we", "us", or "our") is committed to protecting the
          privacy of users of our care scheduling and management application ("Application").
          This Privacy Policy explains what information we collect, how we use it, and your
          rights regarding that information.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account Information:</strong> Name and email address used to create and
            authenticate your account via Google Sign-In.
          </li>
          <li>
            <strong>Scheduling Data:</strong> Shift records including dates, times, client names,
            and caretaker assignments.
          </li>
          <li>
            <strong>Care Reports:</strong> Information submitted by caretakers including
            mileage, supplies, care notes, medication logs, meal records, and custom
            question responses.
          </li>
          <li>
            <strong>Receipt Images:</strong> Photographs of receipts uploaded by caretakers
            in connection with care report submissions.
          </li>
          <li>
            <strong>Client and Caretaker Profiles:</strong> Names, contact details, billing
            rates, and pay rates for clients and caretakers managed within the Application.
          </li>
          <li>
            <strong>Usage Data:</strong> Log data such as access times and pages visited,
            collected automatically by our infrastructure provider.
          </li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, operate, and maintain the Application;</li>
          <li>Generate invoices, payroll reports, and billing summaries;</li>
          <li>Display care history to authorized administrators and family members;</li>
          <li>Sync billing and payroll data with QuickBooks at the administrator's direction;</li>
          <li>Improve and troubleshoot the Application;</li>
          <li>Communicate with you regarding your account or the Application.</li>
        </ul>

        <h2>3. Data Storage and Security</h2>
        <p>
          All data is stored using Supabase, a third-party cloud database and storage
          provider. Data is encrypted in transit using TLS and at rest using industry-standard
          encryption. Receipt images are stored in Supabase Storage with access controlled
          by authentication policies.
        </p>
        <p>
          We implement reasonable security measures to protect your information, but no
          method of transmission over the internet or electronic storage is 100% secure.
        </p>

        <h2>4. Data Sharing</h2>
        <p>We do not sell your personal information. We may share data with:</p>
        <ul>
          <li>
            <strong>Supabase:</strong> Our database and file storage provider, which processes
            data on our behalf under their own privacy and security policies.
          </li>
          <li>
            <strong>Intuit QuickBooks:</strong> When you choose to sync invoice or payroll
            data, the relevant billing information is transmitted to QuickBooks via their API.
            This is initiated explicitly by an administrator.
          </li>
          <li>
            <strong>Google:</strong> Authentication is handled via Google OAuth. We receive
            only your email address from Google during sign-in.
          </li>
          <li>
            <strong>Legal Requirements:</strong> We may disclose information if required by
            law or in response to valid legal process.
          </li>
        </ul>

        <h2>5. Data Retention</h2>
        <p>
          We retain your data for as long as your account is active or as needed to provide
          the Application. You may request deletion of your data by contacting us. Some
          records may be retained as required by applicable law or for legitimate business
          purposes such as accounting records.
        </p>

        <h2>6. Your Rights</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul>
          <li>Access the personal information we hold about you;</li>
          <li>Request correction of inaccurate information;</li>
          <li>Request deletion of your personal information;</li>
          <li>Object to or restrict certain processing of your data.</li>
        </ul>
        <p>
          To exercise these rights, contact us at{' '}
          <a href="mailto:daniel@effectual.tools" className="legalLink">daniel@effectual.tools</a>.
        </p>

        <h2>7. Children's Privacy</h2>
        <p>
          The Application is not directed at individuals under the age of 18. We do not
          knowingly collect personal information from minors.
        </p>

        <h2>8. Third-Party Links</h2>
        <p>
          The Application may contain links to third-party services (such as QuickBooks).
          We are not responsible for the privacy practices of those services and encourage
          you to review their privacy policies.
        </p>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of
          material changes by updating the "Last updated" date above. Continued use of the
          Application after changes constitutes acceptance of the revised policy.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          If you have questions or concerns about this Privacy Policy, please contact us at:{' '}
          <a href="mailto:daniel@effectual.tools" className="legalLink">daniel@effectual.tools</a>
        </p>
      </div>
    </div>
  );
}
